import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartPageConfig } from '@front-platform/content-builder';
import { kioskCartPageResolveConfig } from '@front-platform/content-builder';
import { Component, ElementRef, HostBinding, OnInit, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { Meta } from '@angular/platform-browser';
import { CartService } from '@core/services/cart/cart.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
export interface KioskCartPageRuntimeConfig {
  'body'?: any;
  'footer'?: any;
  'header'?: unknown[] | undefined;
  'meta_description'?: string | undefined;
}

export type KioskCartPageConfigInput = Partial<
  Omit<KioskCartPageConfig, keyof KioskCartPageRuntimeConfig> & KioskCartPageRuntimeConfig
>;
export type KioskCartPageResolvedConfig = Omit<
  KioskCartPageConfig,
  keyof KioskCartPageRuntimeConfig
> &
  Partial<KioskCartPageRuntimeConfig>;
export interface KioskCartPageContract extends ConfigComponentContract<
  KioskCartPageConfigInput,
  KioskCartPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-page',
  templateUrl: './kiosk-cart-page.component.html',
  styleUrls: ['./kiosk-cart-page.component.scss'],
  standalone: false,
})
export class KioskCartPageComponent implements OnInit, KioskCartPageContract {
  readonly config = input<KioskCartPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<KioskCartPageConfig, KioskCartPageRuntimeConfig>(
    kioskCartPageResolveConfig,
    () => ({ config: this.config() }),
  );
  get body(): any {
    return this.resolvedConfig()['body'] as unknown as any;
  }
  get footer(): any {
    return this.resolvedConfig()['footer'] as unknown as any;
  }

  get title(): string | undefined {
    return this.resolvedConfig()['title'] as unknown as string | undefined;
  }
  get primaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get header(): unknown[] | undefined {
    return this.resolvedConfig()['header'] as unknown as unknown[] | undefined;
  }

  get metaDescription(): string | undefined {
    return this.resolvedConfig()['meta_description'] as unknown as string | undefined;
  }

  @HostBinding('class') classes = 'h-screen flex';

  protected readonly errorMessages$ = this.errorService.getErrorMessage$();
  protected readonly isOrderValidation$ = this.orderStoreSelector.selectIsValidating$;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly el: ElementRef,
    private readonly meta: Meta,
    private readonly cartService: CartService,
    private readonly errorService: ErrorService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly orderStoreSelector: OrderStoreSelector,
  ) {
    this.cartService.loadShopsOfCart$().pipe(takeUntilDestroyed()).subscribe();
    this.orderDispatcher.checkCartIsValid();
  }

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      '/' + this.activatedRoute.snapshot.url.join('/'),
      this.activatedRoute.snapshot,
    );

    if (this.textColor?.value) {
      this.setTextOverrideColor();
    }
    if (this.metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: this.metaDescription,
      });
    }

    // this.modalSelector.selectModal(KioskSuggestionsModalComponent.handle);
    // this.modalsService.open(KioskSuggestionsModalComponent.handle);
  }

  setTextOverrideColor(): void {
    const textColor = this.textColor?.value;
    this.el.nativeElement.style.setProperty('--my-class-bg-color', textColor);
  }

  get hasBackgroundImage(): boolean {
    return !!this.backgroundImage?.url;
  }

  getBackgroundStyles() {
    const url = this.backgroundImage?.url;
    return {
      'background-image': url ? `url(${url})` : null,
      'background-size': url ? 'cover' : null,
      'background-position': url ? 'center' : null,
      'background-repeat': url ? 'no-repeat' : null,
    };
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartPageComponent, { 'keyAliases': {} });
